const CONFIG = {
	dimensions: {
		margin: 10,
		flagWidth: 90,
		flagHeight: 90,
		flagX: 100,
		flagY: 0,
		textX: 90 + 30, // flagWidth + margin * 3
		textY: 5,
		rectWidth: 300,
	},
	colors: {
		background: "#ffffff",
		text: "#ffffff",
	},
	layouts: {
		square: {
			height: 200,
			width: 200,
		},
		rectangle: {
			height: 130,
			width: 300,
		},
	},
};

// Error handling utility
const handleError = (error, context) => {
	console.error(`Error in ${context}:`, error);
	// You could add error reporting service here
};

// Add a debounce utility for performance
const debounce = (func, wait) => {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

// Move these class definitions to the top of the file, after CONFIG
class TextManager {
	constructor(canvas) {
		this.canvas = canvas;
		this.textObjects = new Map();
	}

	createText(id, options) {
		try {
			const text = new fabric.IText("", {
				lockMovementY: true,
				lockMovementX: true,
				selectable: false,
				hasControls: false,
				visible: false,
				...options,
			});

			this.textObjects.set(id, text);
			return text;
		} catch (error) {
			handleError(error, "TextManager.createText");
		}
	}

	updateText(id, value) {
		try {
			const text = this.textObjects.get(id);
			if (!text) return;

			text.set("text", value);
			text.set("visible", Boolean(value));
			this.canvas.requestRenderAll();
		} catch (error) {
			handleError(error, "TextManager.updateText");
		}
	}
}

class EventManager {
	constructor() {
		this.events = new Map();
	}

	on(event, callback) {
		if (!this.events.has(event)) {
			this.events.set(event, new Set());
		}
		this.events.get(event).add(callback);
	}

	emit(event, data) {
		if (this.events.has(event)) {
			this.events.get(event).forEach((callback) => {
				try {
					callback(data);
				} catch (error) {
					handleError(error, `EventManager.emit(${event})`);
				}
			});
		}
	}
}

// Handle canvas visibility and PNG preview
function handleCanvasVisibility() {
	try {
		const editorWrap = document.querySelector(".editor-wrap canvas");
		const pngPreview = document.querySelector("#pngHash");
		const tabs = document.querySelector(".editor-tabs");
		const footer = document.querySelector("footer");
		if (!editorWrap || !pngPreview || !tabs) {
			console.log("Missing elements:", { editorWrap, pngPreview, tabs });
			return;
		}
		pngPreview.classList.add("hidden");

		// Track visibility state of both elements
		let isCanvasVisible = true;
		let isTabsVisible = false;
		let isFooterVisible = false;

		// Function to update PNG preview visibility based on both conditions
		const updatePngPreviewVisibility = () => {
			console.log("Visibility states:", {
				isCanvasVisible,
				isTabsVisible,
				isFooterVisible,
			});

			if (isCanvasVisible || isFooterVisible || !isTabsVisible) {
				console.log("Showing PNG preview");
				pngPreview.classList.add("hidden");
			} else {
				console.log("Hiding PNG preview");
				pngPreview.classList.remove("hidden");
			}
		};

		const observer = new IntersectionObserver(
			([entry]) => {
				console.log("Canvas intersection:", entry.isIntersecting);
				isCanvasVisible = entry.isIntersecting;
				updatePngPreviewVisibility();
			},
			{
				threshold: 0,
				rootMargin: "0px 0px 0px 0px",
			}
		);

		const tabsObserver = new IntersectionObserver(
			([entry]) => {
				console.log("Tabs intersection:", entry.isIntersecting);
				isTabsVisible = entry.isIntersecting;
				updatePngPreviewVisibility();
			},
			{
				threshold: 0,
				rootMargin: "0px",
			}
		);
		const footerObserver = new IntersectionObserver(
			([entry]) => {
				console.log("Tabs intersection:", entry.isIntersecting);
				isFooterVisible = entry.isIntersecting;
				updatePngPreviewVisibility();
			},
			{
				threshold: 0.5,
				rootMargin: "0px",
			}
		);
		footerObserver.observe(footer);
		tabsObserver.observe(tabs);
		observer.observe(editorWrap);

		// Cleanup function
		return () => {
			observer.disconnect();
			tabsObserver.disconnect();
		};
	} catch (error) {
		handleError(error, "handleCanvasVisibility");
	}
}

// Initialize visibility handling after canvas is loaded
window.addEventListener("load", () => {
	//handleCanvasVisibility();
});

// Initialize event manager before canvas
const eventManager = new EventManager();

// Canvas initialization function
function initializeCanvas() {
	try {
		const canvas = new fabric.Canvas("canvas");

		canvas.setDimensions({
			width: CONFIG.dimensions.rectWidth,
			height: CONFIG.dimensions.flagHeight + 40,
		});

		canvas.set({
			side: "left",
			uppercase: false,
			layout: "rectangle",
		});

		return canvas;
	} catch (error) {
		handleError(error, "initializeCanvas");
		throw new Error("Failed to initialize canvas");
	}
}

// Declare these at the top level, after the class definitions
let canvas, text, text2, text3, rect, flag;

// Update the canvas initialization
try {
	canvas = initializeCanvas();

	const imgObj = new Image();

	flag = new fabric.Image(imgObj, {
		left: CONFIG.dimensions.flagX,
		top: CONFIG.dimensions.flagY,
		width: CONFIG.dimensions.flagWidth,
		height: CONFIG.dimensions.flagHeight,
		hasControls: false,
		selectable: false,
		lockMovementY: true,
		lockMovementX: true,
	});

	const textManager = new TextManager(canvas);

	text = textManager.createText("text1", {
		top: CONFIG.dimensions.textY,
		left: CONFIG.dimensions.textX,
		fontSize: 110,
		fill: CONFIG.colors.text,
	});

	text2 = textManager.createText("text2", {
		top: CONFIG.dimensions.textY + 50,
		left: CONFIG.dimensions.textX,
		fontSize: 60,
		fill: CONFIG.colors.text,
	});

	text3 = textManager.createText("text3", {
		top: CONFIG.dimensions.textY + CONFIG.dimensions.textY + 50,
		left: CONFIG.dimensions.textX,
		fontSize: 40,
		fill: CONFIG.colors.text,
	});

	rect = new fabric.Rect({
		left: 0,
		top: 0,
		width: canvas.width,
		height: CONFIG.dimensions.flagHeight + 40,
		fill: CONFIG.colors.background,
		selectable: false,
		rx: CONFIG.dimensions.margin,
		ry: CONFIG.dimensions.margin,
	});

	// Add objects to canvas
	canvas.add(rect);
	canvas.add(flag);
	canvas.add(text);
	canvas.add(text2);
	canvas.add(text3);
} catch (error) {
	handleError(error, "Canvas initialization");
	// Check if element exists before setting innerHTML
	const container = document.getElementById("canvas-container");
	if (container) {
		container.innerHTML =
			'<div class="error-message">Failed to load editor. Please refresh the page.</div>';
	}
}

var toggleSideElement = document.querySelector(".toggle-side div");
var toggleCaseElement = document.querySelector(".toggle-case div");

if (!canvas.micro) {
	updateIcon();
}

function resizeCanvas() {
	var containerElement = document.querySelector(".editor");
	var canvasElement = document.querySelector(".canvas-container");
	if (canvas.width > containerElement.offsetWidth) {
		scale = Math.min(containerElement.offsetWidth / canvas.width) * 0.85;
		canvasElement.style.transform = "scale(" + scale + ")";
	} else {
		canvasElement.style.transform = "scale(1)";
	}
}

function syncWidth(textObject) {
	try {
		var textOriginal = textObject.text;
		var textRevised = textOriginal
			.replace(/(\r\n|\n|\r)/gm, " ")
			.replace(/[^a-zA-Z0-9åäöüÅÄÖÜßÀ-ÿ@#.,-_ ]"/g, "")
			.trim();

		textObject.set({
			text: textRevised,
		});

		var maxWidth;

		if (rect.height < 131) {
			maxWidth =
				rect.width - CONFIG.dimensions.textX - CONFIG.dimensions.margin * 2;
		} else {
			maxWidth = rect.width - CONFIG.dimensions.margin * 2;
		}

		if (canvas.extended) {
			maxWidth = rect.width - 40;
		}

		const scale = Math.min(maxWidth / textObject.width);

		if (scale < 1) {
			textObject.set("scaleX", scale);
			textObject.set("scaleY", scale);
		} else {
			textObject.set("scaleX", 1);
			textObject.set("scaleY", 1);
		}

		//textObject.set("scaleY", syncHeight(textObject, scale));
	} catch (error) {
		handleError(error, "syncWidth");
	}
}

function addIcon(code, name) {
	var option = document.createElement("img");
	option.dataset.code = name;
	option.src = code;
	return option;
}

function updateName(updateSession) {
	try {
		// Get input elements
		const inputs = {
			firstRow: document.querySelector("#firstRow"),
			secondRow: document.querySelector("#secondRow"),
			thirdRow: document.querySelector("#thirdRow"),
		};

		// Get values and filter empty ones
		const textValues = {
			text1: inputs.firstRow?.value || "",
			text2: inputs.secondRow?.value || "",
			text3: inputs.thirdRow?.value || "",
		};

		// Handle session storage
		if (updateSession) {
			Object.entries(textValues).forEach(([key, value]) => {
				sessionStorage.setItem(key, value);
			});
		}

		// Filter out empty values
		const textValueFiltered = Object.values(textValues).filter(Boolean);

		// Check if second row is actually visible in the DOM
		const isSecondRowVisible =
			inputs.secondRow &&
			window.getComputedStyle(inputs.secondRow).display !== "none" &&
			!inputs.secondRow.disabled;

		// Set placeholder text based on visible inputs
		if (textValueFiltered.length === 0) {
			if (canvas.micro || !isSecondRowVisible) {
				textValueFiltered.push("Förnamn");
				text2.set({ visible: false });
				text3.set({ visible: false });
			} else {
				textValueFiltered.push("Förnamn");
				if (isSecondRowVisible) {
					textValueFiltered.push("Efternamn");
				}
			}
		}
		const availableHeight = canvas.height >= 130 ? 130 : canvas.height;
		// Rest of the positioning logic
		if (textValueFiltered[2] && !canvas.micro) {
			// Three lines of text
			text.set({
				top: (availableHeight / 4) * 1,
				fontSize: 30,
				originY: "center",
				text: textValueFiltered[0],
				visible: true,
			});
			text2.set({
				text: textValueFiltered[1],
				fontSize: 30,
				visible: true,
				top: (availableHeight / 4) * 2,
				originY: "center",
			});
			text3.set({
				text: textValueFiltered[2],
				fontSize: 30,
				visible: true,
				top: (availableHeight / 4) * 3,
				originY: "center",
			});

			// Handle square layout positioning
			if (canvas.layout == "square" && canvas.extended) {
				text.set({
					top: (canvas.height / 4) * 1,
					originY: "center",
				});
				text2.set({
					top: (canvas.height / 4) * 2,
					originY: "center",
				});
				text3.set({
					top: (canvas.height / 4) * 3,
					originY: "center",
				});
			} else if (canvas.layout == "square" && !canvas.extended) {
				canvas.getObjects("i-text").forEach(function (item, i) {
					item.set("originY", "top");
				});
			}
		} else if (textValueFiltered[1] && !canvas.micro && isSecondRowVisible) {
			// Two lines of text
			text.set({
				top: (availableHeight / 3) * 1,
				fontSize: 45,
				originY: "center",
				text: textValueFiltered[0],
				visible: true,
			});
			text2.set({
				text: textValueFiltered[1],
				fontSize: 45,
				visible: true,
				top: (availableHeight / 3) * 2,
				originY: "center",
			});
			text3.set({
				visible: false,
			});

			if (canvas.layout == "square" && canvas.extended) {
				text.set({
					top: canvas.height / 3,
					originY: "center",
				});
				text2.set({
					top: (canvas.height / 3) * 2,
					originY: "center",
				});
			}
		} else {
			// Single line of text
			text.set({
				top: availableHeight / 2,
				fontSize: 90,
				originY: "center",
				text: textValueFiltered[0],
				visible: true,
			});

			// Always hide second and third text in single line mode
			text2.set({
				visible: false,
				text: "", // Clear the text to prevent any ghost text
			});
			text3.set({
				visible: false,
				text: "", // Clear the text to prevent any ghost text
			});

			if (canvas.layout == "square" && canvas.extended) {
				text.set({
					top: canvas.height / 2,
					originY: "center",
				});
			}
		}

		canvas.requestRenderAll();
	} catch (error) {
		handleError(error, "updateName");
	}
}

function changeTheme(bgColor, textColor, bgImage, icon) {
	if (!bgImage) {
		if (canvas.backgroundImage) {
			canvas.backgroundImage.set({
				visible: false,
			});
		}
		rect.set({
			visible: true,
		});
	} else {
		rect.set({
			visible: false,
		});

		setBackgroundImage(bgImage, true);
	}

	if (icon) {
		updateIcon(icon);
		sessionStorage.setItem("icon", icon.src);
	}
	if (textColor && textColor != "#") {
		sessionStorage.setItem("textcolor", textColor);

		setTextColor(textColor, true);
	}
	if (bgColor && bgColor != "#") {
		setBackgroundColor(bgColor, true);
	}
	// //console.log(icon, bgImage, bgColor, textColor);
}

function setTextColor(color, saveToSessionStorage) {
	try {
		if (!canvas) return;

		canvas.getObjects("i-text").forEach(function (item) {
			item.set({
				fill: color,
			});
		});

		if (saveToSessionStorage) {
			sessionStorage.setItem("textcolor", color);
		}

		canvas.getObjects("i-text").forEach(function (item) {
			item.visible = true;
		});

		canvas.requestRenderAll();
	} catch (error) {
		handleError(error, "setTextColor");
	}
}

function setBackgroundColor(color, saveToSessionStorage) {
	//console.log(color);
	rect.visible = true;
	canvas.backgroundImage = null;
	if (typeof color == "object") {
		canvas.gradient = true;
		var gradient = new fabric.Gradient({
			type: "linear",
			gradientUnits: "pixels", // or 'percentage'
			coords: { x1: 0, y1: 0, x2: 0, y2: rect.height },
			colorStops: [
				{ offset: 0, color: color[1] },
				{ offset: 1, color: color[0] },
			],
		});
		rect.set("fill", gradient);
	} else {
		rect.set({
			fill: color,
		});
		if (saveToSessionStorage) {
			sessionStorage.setItem("backgroundcolor", color);
		}
	}

	canvas.requestRenderAll();
}

function changeLayout(layout) {
	try {
		var h, w, tx, ty, t2x, t2y, t3x, t3y, fy, fx, fyo, fxo;

		if (layout == "square") {
			h = CONFIG.layouts.square.height;
			w = CONFIG.layouts.square.width;
			tx = w / 2;
			t2x = w / 2;
			fy = h - CONFIG.dimensions.margin;
			fx = w / 2; // Center flag horizontally
			fyo = "bottom";
			fxo = "center";

			// Calculate and apply flag scaling for square mode
			const scaleX = CONFIG.dimensions.flagWidth / flag.width;
			const scaleY = CONFIG.dimensions.flagHeight / flag.height;
			const scale = Math.min(scaleX, scaleY) * 0.7; // 70% of original size
			flag.scale(scale);

			// Adjust text positioning for square layout
			const textSpacing = h / 4;
			text.set({
				left: tx,
				originX: "center",
				top: textSpacing - 45,
				originY: "center",
				fontSize: canvas.micro ? 60 : 45,
			});

			text2.set({
				left: tx,
				originX: "center",
				top: textSpacing * 2,
				originY: "center",
				fontSize: canvas.micro ? 40 : 35,
			});

			text3.set({
				left: tx,
				originX: "center",
				top: textSpacing * 3,
				originY: "center",
				fontSize: canvas.micro ? 30 : 25,
			});
		} else if (layout == "rectangle") {
			h = CONFIG.layouts.rectangle.height;
			w = CONFIG.dimensions.rectWidth;
			tx = CONFIG.dimensions.textX;
			ty = CONFIG.dimensions.textY;
			t2x = CONFIG.dimensions.textX;
			t2y = ty + 50;
			t3y = ty + 90;
			fy = h / 2;
			fx = CONFIG.dimensions.margin + 50;

			fyo = "center";
			fxo = "left";

			// Reset text positioning for rectangle layout
			text.set({
				left: tx,
				originX: fxo,
				top: ty,
				originY: "top",
				fontSize: canvas.micro ? 90 : 110,
			});

			text2.set({
				left: tx,
				originX: fxo,
				top: t2y,
				originY: "top",
				fontSize: 60,
			});

			text3.set({
				left: tx,
				originX: fxo,
				top: t3y,
				originY: "top",
				fontSize: 40,
			});

			// Reset flag scale for rectangle mode
			const scaleX = CONFIG.dimensions.flagWidth / flag.width;
			const scaleY = CONFIG.dimensions.flagHeight / flag.height;
			const scale = Math.min(scaleX, scaleY);
			flag.scale(scale);
		}

		if (canvas.extended) {
			tx = canvas.width / 2;
			t2x = canvas.width / 2;
			fxo = "center";

			// Adjust text positioning for extended mode
			canvas.getObjects("i-text").forEach(function (item) {
				item.set({
					left: tx,
					originX: "center",
				});
			});
		}

		canvas.setDimensions({
			height: h,
			width: w,
		});
		document.documentElement.style.setProperty("--canvas-height", h + "px");
		document.documentElement.style.setProperty("--canvas-width", w + "px");
		rect.set({
			height: h,
			width: w,
		});

		flag.set({
			top: fy,
			left: fx,
			originY: fyo,
			originX: "center",
		});

		canvas.set({
			layout: layout,
		});

		if (
			canvas.side == "right" &&
			canvas.layout == "rectangle" &&
			!canvas.micro
		) {
			resetSide();
			toggleSide();
		}

		updateName();
		if (canvas.backgroundImage) {
			resizeBackground();
		}

		canvas.requestRenderAll();
	} catch (error) {
		handleError(error, "changeLayout");
	}
}

function updateIcon(url, saveToSessionStorage) {
	console.log("updateIcon", canvas.micro);
	try {
		if (url && !canvas.micro) {
			document.documentElement.style.setProperty("--canvas-has-icon", "1");
			if (saveToSessionStorage) {
				sessionStorage.setItem("icon", url.src);
			}

			flag.setElement(url);
			flag.set({
				visible: true,
				scaleX: 1,
				scaleY: 1,
				originX: "center",
				originY: "center",
			});

			// Calculate scaling factors for both dimensions
			const scaleX = CONFIG.dimensions.flagWidth / flag.width;
			const scaleY = CONFIG.dimensions.flagHeight / flag.height;

			// Use a smaller scale factor for square layout
			const scale =
				canvas.layout == "square"
					? Math.min(scaleX, scaleY) * 0.7 // Scale down by 30% in square mode
					: Math.min(scaleX, scaleY);

			flag.scale(scale);

			// Center the flag based on layout
			if (canvas.layout === "square") {
				flag.set({
					top: canvas.height - CONFIG.dimensions.margin,
					left: canvas.width / 2,
				});
			} else {
				const baseX =
					canvas.side === "left"
						? CONFIG.dimensions.margin + CONFIG.dimensions.flagWidth / 2
						: canvas.width -
						  CONFIG.dimensions.margin -
						  CONFIG.dimensions.flagWidth / 2;

				flag.set({
					top: canvas.height / 2,
					left: baseX,
				});
			}
			document.documentElement.style.setProperty("--canvas-has-icon", 1);
			canvas.set("extended", false);
		} else {
			flag.set({
				visible: false,
				flipX: false,
			});
			document.documentElement.style.setProperty("--canvas-has-icon", 0);
			document.documentElement.setAttribute("data-canvas-side", "left");
			canvas.set("extended", true);
		}

		if (canvas.height < 131) {
			changeLayout("rectangle");
		} else {
			changeLayout("square");
		}

		canvas.renderAll();
	} catch (error) {
		handleError(error, "updateIcon");
	}
}

function resetSide() {
	if (!canvas.micro) {
		//console.log('resetSide')
		canvas.set({
			side: "left",
		});
		//toggleSideElement.style.left = 0;
	}
}

function setBackgroundImage(url, layout) {
	rect.visible = false;
	canvas.setBackgroundImage(url, resizeBackground.bind(canvas), {
		backgroundImageStretch: false,
		originX: "center",
		originY: "center",
		top: canvas.height / 2,
		left: canvas.width / 2,
		crossOrigin: "anonymous",
	});
}

function toggleSide() {
	try {
		if (
			canvas.side == "left" &&
			!canvas.extended &&
			canvas.layout == "rectangle"
		) {
			flag.set({
				originX: "center",
				left: canvas.width - CONFIG.dimensions.margin - 50,
				flipX: true,
			});
			canvas.getObjects("i-text").forEach(function (item, i) {
				item.set({ left: CONFIG.dimensions.margin });
			});

			canvas.set("side", "right");
			document.documentElement.setAttribute("data-canvas-side", "right");
		} else if (
			canvas.side == "right" &&
			!canvas.extended &&
			canvas.layout == "rectangle"
		) {
			flag.set({
				originX: "center",
				left: CONFIG.dimensions.margin + 50,
				flipX: false,
			});

			canvas.getObjects("i-text").forEach(function (item, i) {
				item.set({ left: CONFIG.dimensions.textX });
			});
			canvas.set("side", "left");

			document.documentElement.setAttribute("data-canvas-side", "left");
		}
		canvas.requestRenderAll();
	} catch (error) {
		handleError(error, "toggleSide");
	}
}

function resizeBackground() {
	canvas.backgroundImage.set({
		top: canvas.height / 2,
		left: canvas.width / 2,
	});

	canvas.backgroundImage.scaleToHeight(canvas.height);

	canvas.requestRenderAll();
}

// Render handling with performance optimization
const afterRender = debounce(() => {
	try {
		canvas.off("after:render", afterRender);

		// Dispatch canvasLoaded event
		window.dispatchEvent(new Event("canvasLoaded"));

		const pngHash = canvas.toDataURL({ multiplier: 1 });
		const jsonData = JSON.stringify(canvas.toJSON());
		const textData = [text.text, text2.text, text3.text]
			.filter(Boolean)
			.join(", ");

		// Update DOM elements
		const elements = {
			base64: document.querySelector("#base64"),
			json: document.querySelector("#json"),
			inputData: document.querySelector("#inputData"),
			pngHash: document.querySelector("#pngHash img"),
		};
		//console.log(elements.pngHash);
		if (elements.base64) elements.base64.value = pngHash;
		if (elements.json) elements.json.value = jsonData;
		if (elements.inputData) elements.inputData.value = textData;
		if (elements.pngHash) elements.pngHash.src = pngHash;

		// Emit event for external listeners
		eventManager.emit("renderComplete", { pngHash, jsonData, textData });

		canvas.on("after:render", afterRender);
	} catch (error) {
		handleError(error, "afterRender");
	}
}, 100);

// Initial render event binding
canvas.on("after:render", afterRender);

canvas.on("before:render", function () {
	canvas.getObjects("i-text").forEach(function (item, i) {
		syncWidth(item);
	});
});
canvas.on("mouse:up", function (options) {
	//console.log('mouse up');
	document.querySelector("#firstRow").focus();
});

function changeFont(font) {
	if (font !== "Times New Roman") {
		loadAndUse(font);
		canvas.requestRenderAll();
	} else {
		canvas.getActiveObject().set("fontFamily", font);
		canvas.requestRenderAll();
	}
}

function toggleCase() {
	//console.log('toggleCase')
	if (!canvas.uppercase) {
		toggleCaseElement.style.left = "50%";
	} else {
		toggleCaseElement.style.left = 0;
	}
	canvas.set("uppercase", !canvas.uppercase);
	updateName();
}

function resizeCanvas() {
	var containerElement = document.querySelector(".editor-wrap");
	var canvasElement = document.querySelector(".canvas-container");
	var paddedBox = containerElement.offsetWidth - 100;
	if (canvas.width > containerElement.offsetWidth) {
		//console.log(canvas.width);
		//console.log(containerElement.offsetWidth)
		scale = Math.min(containerElement.offsetWidth / canvas.width);
		////console.log(scale)
		//canvasElement.style.transform = "scale(" + scale + ")";
	} else {
		canvasElement.style.transform = "scale(1)";
	}
}

window.addEventListener("resize", resizeCanvas);
function goToNextTab() {
	const tabButton = document.querySelector("li:has(.t4s-active) + li a");
	console.log(tabButton);
	if (tabButton) {
		const clickEvent = new PointerEvent("click");
		tabButton.dispatchEvent(clickEvent);
	}
}
function goToPrevTab() {
	const tabButton = document.querySelector("li:has(+ li .t4s-active) a");
	if (tabButton) {
		const clickEvent = new PointerEvent("click");
		tabButton.dispatchEvent(clickEvent);
	}
	console.log(tabButton);
}

//loadThemes();
/*
document.getElementById('imgLoader').onchange = function handleImage(e) {
  var reader = new FileReader();
  reader.onload = function (event){
    var imgObj = new Image();
    imgObj.src = event.target.result;

    imgObj.onload = function () {
      updateIcon(event.target.result);

      canvas.renderAll();
    }
  }
  reader.readAsDataURL(e.target.files[0]);
}*/

// Improve resize handling
const debouncedResize = debounce(() => {
	try {
		resizeCanvas();
		eventManager.emit("canvasResized");
	} catch (error) {
		handleError(error, "resize");
	}
}, 250);

window.addEventListener("resize", debouncedResize);

class EditorState {
	constructor() {
		this.state = {
			layout: "rectangle",
			side: "left",
			uppercase: false,
			extended: false,
			textColor: CONFIG.colors.text,
			backgroundColor: CONFIG.colors.background,
		};

		this.listeners = new Set();
	}

	setState(updates) {
		const oldState = { ...this.state };
		this.state = { ...this.state, ...updates };
		this.notifyListeners(oldState);
	}

	addListener(callback) {
		this.listeners.add(callback);
	}

	notifyListeners(oldState) {
		this.listeners.forEach((listener) => {
			try {
				listener(this.state, oldState);
			} catch (error) {
				handleError(error, "EditorState.notifyListeners");
			}
		});
	}
}

const editorState = new EditorState();

function canvasLoaded(shape) {
	try {
		if (shape == "micro") {
			canvas.micro = true;
			if (rect) {
				rect.set({ ry: 20, rx: 20 });
			}
			updateName();
		}
		if (shape == "square") {
			changeLayout("square");
		}
	} catch (error) {
		handleError(error, "canvasLoaded");
	}
}
function scrollToCanvas(e) {
	console.log("scrollToCanvas");
	setTimeout(() => {
		document.querySelector(".canvas-container").scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	}, 50);
}

function scrollToTabs() {
	console.log("scrollToTabs");

	setTimeout(() => {
		document.querySelector(".editor-controls").scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
	}, 50);
}
document.querySelectorAll(".editor-controls input").forEach((el) => {
	el.addEventListener("focus", scrollToCanvas);
});
const el = document.querySelector(".editor-wrap");
let scrollTimeout;
window.addEventListener("scroll", () => {
	console.log("scroll");
	// Debounce the scroll event
	if (scrollTimeout) {
		window.cancelAnimationFrame(scrollTimeout);
	}

	scrollTimeout = window.requestAnimationFrame(() => {
		const stickyTop = parseInt(window.getComputedStyle(el).top);
		const coords = el.getBoundingClientRect();
		const currentTop = coords.top;

		// Add a small threshold (2px) to prevent jumping
		const isNearThreshold = Math.abs(currentTop - stickyTop) <= 2;
		el.classList.toggle("is-sticky", isNearThreshold);
	});
});
